import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Mic,
  Copy,
  FileAudio,
  Users,
  BarChart3,
  User,
  LogOut,
  Menu,
  X,
  Home,
  Mail,
  Ticket
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { APP_FEATURES } from '../types';

const Layout = () => {
  const { t: tLayout } = useTranslation('layout');
  const { t: tCommon } = useTranslation('common');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: tLayout('nav.dashboard'), icon: Home },
    { path: '/dashboard/voice-design', label: tLayout('nav.voiceDesign'), icon: Mic },
    { path: '/dashboard/voice-clone', label: tLayout('nav.voiceClone'), icon: Copy },
    { path: '/dashboard/transcribe', label: tLayout('nav.transcribe'), icon: FileAudio },
  ];

  const adminItems = [
    { path: '/dashboard/users', label: tLayout('admin.userManagement'), icon: Users },
    { path: '/dashboard/stats', label: tLayout('admin.stats'), icon: BarChart3 },
    { path: '/dashboard/invitation-codes', label: tLayout('admin.invitationCodes'), icon: Ticket },
    ...(APP_FEATURES.emailEnabled ? [{ path: '/dashboard/email-config', label: tLayout('admin.emailConfig'), icon: Mail }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 移动端菜单按钮 */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-white rounded-lg shadow-md"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* 侧边栏 */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-primary-600">MYAIGC</h1>
            <p className="text-sm text-gray-500">{tLayout('subtitle')}</p>
          </div>

          {/* 导航 */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {isAdmin && (
              <>
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {tLayout('admin.label')}
                  </p>
                  {adminItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sidebar-link ${isActive ? 'active' : ''}`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon size={20} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </nav>

          {/* 用户信息 */}
          <div className="p-4 border-t border-gray-200">
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User size={20} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.username}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'admin' ? tCommon('roles.admin') : user?.role === 'vip' ? tCommon('roles.vip') : tCommon('roles.user')}
                </p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors w-full mt-2"
            >
              <LogOut size={20} />
              <span>{tLayout('user.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* 遮罩层 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 主内容区 */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
